// Time-boxed daily calendar view.
import { format } from 'date-fns';
import { useMemo, useRef, useState, useEffect, useId } from 'react';
import { Box } from '@mui/material';
import { useAuth } from '@/features/auth/useAuth';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import useCalendarDays from '@/features/planning/hooks/time-grid/useCalendarDays';
import useTimeSlots from '@/features/planning/hooks/time-grid/useTimeSlots';
import { useCalendarActiveTaskId } from '@/features/planning/hooks/time-grid/useCalendarActiveTaskId';
import { useCalendarNewTaskHandlers } from '@/features/planning/hooks/time-grid/useCalendarNewTaskHandlers';
import useCurrentTimePosition from '@/features/planning/hooks/time-grid/useCurrentTimePosition';
import { useBreakpoint, breakpointOrder } from '@/hooks/ui/useBreakpoint';
import useCalendarViewLogic from '@/features/planning/hooks/time-grid/useCalendarViewLogic';
import useCalendarAutoScroll from '@/features/planning/hooks/time-grid/useCalendarAutoScroll';
import useNewTaskLogic from '@/features/tasks/hooks/mutations/use-task-new-logic';
import useAddTask from '@/features/tasks/hooks/mutations/use-task-add';
import DayColumn from '@/features/planning/components/time-grid/DayColumn';
import TimeLabelColumn from '@/features/planning/components/time-grid/TimeLabelColumn';
import { useCalendarTaskRoving } from '@/features/planning/hooks/time-grid/useCalendarTaskRoving';
import { buildCalendarNavigableTasks } from '@/features/planning/utils/calendarNavigableTasks';
import {
  buildHourlyLabels,
  renderCalendarKeyboardHint,
} from '@/features/planning/components/time-grid/calendarViewParts';
import { useT } from '@/i18n/useT';
import { selectOnboardingTimeSlotHighlight } from '@/features/onboarding/utils/onboardingTimeSlotHighlight';

const SLOT_HEIGHT_FALLBACK_PX = 20;

const CalendarView = ({
  onboardingHighlightTimeSlot = false,
  onboardingResizeHighlightTaskId = null,
}) => {
  const t = useT();
  const { allTasks, filteredTasks } = useTasksContext();
  const [slotHeight, setSlotHeight] = useState(SLOT_HEIGHT_FALLBACK_PX);
  const { user } = useAuth();
  const currentBreakpoint = useBreakpoint();
  const isMobile = breakpointOrder[currentBreakpoint] <= breakpointOrder.s;
  const { addTask } = useAddTask();
  const { augmentNewTaskWithDefaults } = useNewTaskLogic();
  const { newTaskInfo, handleDoubleClick, handleSaveTask, handleCancel } =
    useCalendarNewTaskHandlers({
      allTasks,
      user,
      addTask,
      augmentNewTaskWithDefaults,
    });

  const calendarContainerRef = useRef(null);
  const firstHourLabelRef = useRef(null);
  const days = useCalendarDays();
  const rawNowLinePosition = useCurrentTimePosition(slotHeight);
  const { startHour, endHour, scrollPosition, scrollBehavior } =
    useCalendarViewLogic(isMobile, days, rawNowLinePosition, slotHeight);

  useEffect(() => {
    const hourCount = Math.max(1, endHour - startHour);
    document.documentElement.style.setProperty(
      '--hour-label-count',
      String(hourCount),
    );
  }, [startHour, endHour]);

  const fifteenMinuteSlots = useTimeSlots(startHour, endHour);

  const onboardingTimeSlotHighlight = useMemo(() => {
    if (!onboardingHighlightTimeSlot) {
      return null;
    }
    return selectOnboardingTimeSlotHighlight({
      visibleDayDates: days.map((day) => day.date),
      slotTimes: fifteenMinuteSlots,
      startHour,
      endHour,
    });
  }, [
    onboardingHighlightTimeSlot,
    days,
    fifteenMinuteSlots,
    startHour,
    endHour,
  ]);

  useEffect(() => {
    const rootStyle = getComputedStyle(document.documentElement);
    const slotVar = isMobile
      ? '--time-slot-height-mobile'
      : '--time-slot-height-desktop';
    const heightValue = rootStyle.getPropertyValue(slotVar).trim();
    setSlotHeight(parseInt(heightValue, 10) || SLOT_HEIGHT_FALLBACK_PX);
  }, [isMobile]);

  const firstDayDate = days[0]?.date;
  useCalendarAutoScroll(
    calendarContainerRef,
    scrollPosition,
    scrollBehavior,
    `${isMobile}:${firstDayDate}`,
  );

  const tasksWithNewTask = useMemo(() => {
    if (!filteredTasks) return [];
    if (newTaskInfo) {
      const tempTask = {
        id: 'new-task',
        ...newTaskInfo,
        isNew: true,
      };
      return [...filteredTasks, tempTask];
    }
    return filteredTasks;
  }, [filteredTasks, newTaskInfo]);

  const hourlyLabels = useMemo(
    () => buildHourlyLabels(startHour, endHour),
    [startHour, endHour],
  );

  const currentTaskId = useCalendarActiveTaskId(filteredTasks);
  const navigableTasks = useMemo(
    () => buildCalendarNavigableTasks(tasksWithNewTask, days),
    [tasksWithNewTask, days],
  );
  const { activeTaskId, ariaActiveDescendant, onFocus, onKeyDown } =
    useCalendarTaskRoving(navigableTasks);
  const keyboardHintId = useId();

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {renderCalendarKeyboardHint(
        keyboardHintId,
        t(
          'plan.calendarKeyboardHint',
          'Use arrow keys to move between scheduled tasks in the calendar.',
        ),
      )}
      <Box
        ref={calendarContainerRef}
        data-testid="calendar-scroll"
        tabIndex={0}
        role="listbox"
        aria-label={t('plan.calendarRegion', 'Day calendar')}
        aria-describedby={keyboardHintId}
        aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
        aria-activedescendant={ariaActiveDescendant}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        sx={{
          display: 'flex',
          flexGrow: 1,
          gap: 1.5,
          overflowY: 'auto',
          overflowX: isMobile ? 'hidden' : 'auto',
          position: 'relative',
          minHeight: 0,
          alignItems: 'flex-start',
          outline: 'none',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <TimeLabelColumn
          hourlyLabels={hourlyLabels}
          firstHourLabelRef={firstHourLabelRef}
        />
        {days.map((day) => (
          <DayColumn
            key={day.label || format(day.date, 'yyyy-MM-dd')}
            day={day}
            fifteenMinuteSlots={fifteenMinuteSlots}
            scheduledTasks={tasksWithNewTask}
            currentTaskId={currentTaskId}
            activeTaskId={activeTaskId}
            onDoubleClick={handleDoubleClick}
            onSaveNewTask={handleSaveTask}
            onCancelNewTask={handleCancel}
            fifteenMinuteSlotHeight={slotHeight}
            startHour={startHour}
            endHour={endHour}
            onboardingTimeSlotHighlight={onboardingTimeSlotHighlight}
            onboardingResizeHighlightTaskId={onboardingResizeHighlightTaskId}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CalendarView;
